import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useWebsites } from '@/context/WebsitesContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import config from '@/config';

type Concept = {
  id: string;
  name: string;
  description: string | null;
};

type Keyword = {
  id: string;
  keyword: string;
  concept_id: string | null;
};

type Relation = {
  id: string;
  keyword_id: string;
  sitemap_id: string;
  role: 'primary' | 'supporting';
};

type Page = {
  id: string;
  title: string | null;
  url: string | null;
};

const ConceptsOverview: React.FC = () => {
  const { currentWebsite } = useWebsites();
  const websiteId = currentWebsite?.id;

  const [loading, setLoading] = useState(false);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [relations, setRelations] = useState<Relation[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [editingConceptId, setEditingConceptId] = useState<string | null>(null);
  const [editingConceptName, setEditingConceptName] = useState('');
  const [conceptToDelete, setConceptToDelete] = useState<Concept | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  const pageById = useMemo(() => {
    const map: Record<string, Page> = {};
    for (const p of pages) map[p.id] = p;
    return map;
  }, [pages]);

  const keywordsByConcept = useMemo(() => {
    const map: Record<string, Keyword[]> = {};
    for (const k of keywords) {
      const cid = k.concept_id || 'unassigned';
      if (!map[cid]) map[cid] = [];
      map[cid].push(k);
    }
    return map;
  }, [keywords]);

  const relationsByKeyword = useMemo(() => {
    const map: Record<string, Relation[]> = {};
    for (const r of relations) {
      if (!map[r.keyword_id]) map[r.keyword_id] = [];
      map[r.keyword_id].push(r);
    }
    // Sort primary first
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.role === 'primary' ? -1 : 1));
    }
    return map;
  }, [relations]);

  const loadAll = async () => {
    if (!websiteId) return;
    setLoading(true);
    try {
      const [{ data: c }, { data: k }, { data: p }] = await Promise.all([
        supabase.from('kw_concepts').select('id,name,description').eq('website_id', websiteId).order('name', { ascending: true }),
        supabase.from('kw_keywords').select('id,keyword,concept_id').eq('website_id', websiteId).order('keyword', { ascending: true }),
        supabase.from('website_content').select('id,title,url').eq('website_id', websiteId)
      ]);

      setConcepts(c || []);
      setKeywords(k || []);
      setPages(p || []);

      // load relations for the website keywords
      const keywordIds = (k || []).map(x => x.id);
      if (keywordIds.length > 0) {
        const { data: r } = await supabase
          .from('kw_keyword_sitemap')
          .select('id,keyword_id,sitemap_id,role')
          .in('keyword_id', keywordIds);
        setRelations(r || []);
      } else {
        setRelations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [websiteId]);

  const startEditConcept = (concept: Concept) => {
    setEditingConceptId(concept.id);
    setEditingConceptName(concept.name);
  };

  const saveConcept = async () => {
    if (!editingConceptId) return;
    await supabase.from('kw_concepts').update({ name: editingConceptName, updated_at: new Date().toISOString() }).eq('id', editingConceptId);
    setEditingConceptId(null);
    setEditingConceptName('');
    await loadAll();
  };

  const cancelEdit = () => {
    setEditingConceptId(null);
    setEditingConceptName('');
  };

  const setPrimaryPage = async (keywordId: string, pageId: string) => {
    // Demote existing primary
    await supabase.from('kw_keyword_sitemap').update({ role: 'supporting', updated_at: new Date().toISOString() }).eq('keyword_id', keywordId).eq('role', 'primary');
    // Upsert selected as primary
    await supabase.from('kw_keyword_sitemap').upsert({ keyword_id: keywordId, sitemap_id: pageId, role: 'primary', created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, { onConflict: 'keyword_id,sitemap_id' });
    await loadAll();
  };

  const removeSupporting = async (relationId: string) => {
    await supabase.from('kw_keyword_sitemap').delete().eq('id', relationId);
    await loadAll();
  };

  const handleDeleteConceptDeep = async (concept: Concept) => {
    if (!websiteId) return;
    try {
      setBusy(true);
      // Load keyword ids for this concept
      const { data: kws } = await supabase
        .from('kw_keywords')
        .select('id')
        .eq('website_id', websiteId)
        .eq('concept_id', concept.id);

      const keywordIds = (kws || []).map(k => k.id);
      if (keywordIds.length > 0) {
        await supabase.from('kw_keyword_sitemap').delete().in('keyword_id', keywordIds);
      }
      // Delete keywords
      await supabase.from('kw_keywords').delete().eq('website_id', websiteId).eq('concept_id', concept.id);
      // Clear page assignments referencing this concept
      await supabase
        .from('website_content')
        .update({ concept_id: null, needs_review: false, updated_at: new Date().toISOString() })
        .eq('website_id', websiteId)
        .eq('concept_id', concept.id);
      // Finally delete concept
      await supabase.from('kw_concepts').delete().eq('id', concept.id);
      toast.success('Theme deleted');
      await loadAll();
    } catch (e) {
      console.error('Failed deep delete concept', e);
      toast.error('Failed to delete theme');
    } finally {
      setBusy(false);
      setConceptToDelete(null);
    }
  };

  const handleRedoAnalysis = async () => {
    if (!websiteId) return;
    try {
      setBusy(true);
      // Reset keyword data via Edge Function, then restart analysis
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('No session');
        return;
      }
      const base = config.supabase.functionsUrl;
      const resetRes = await fetch(`${base}/keyword-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ website_id: websiteId, action: 'reset' })
      });
      if (!resetRes.ok) {
        toast.error('Failed to reset data');
        return;
      }
      // Start again
      const startRes = await fetch(`${base}/keyword-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ website_id: websiteId, action: 'force_start' })
      });
      if (!startRes.ok) {
        toast.error('Failed to start analysis');
        return;
      }
      toast.success('Analysis restarted');
      await loadAll();
    } catch (e) {
      console.error('Redo analysis failed', e);
      toast.error('Redo analysis failed');
    } finally {
      setBusy(false);
      setShowResetDialog(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Themes</CardTitle>
          {concepts.length > 0 && (
            <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={busy}>
                  Redo analysis
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Redo analysis?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove all themes, keywords, and page assignments for this website and restart the analysis. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRedoAnalysis} disabled={busy}>
                    Yes, redo analysis
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {concepts.length === 0 && (
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">No concepts found. Run keyword analysis to generate concepts and assignments.</div>
            <Button
              size="sm"
              onClick={async () => {
                if (!websiteId) return;
                // trigger keyword-analysis start
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.access_token) return;
                await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/keyword-analysis`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                  },
                  body: JSON.stringify({ website_id: websiteId, action: 'start' })
                });
                setTimeout(loadAll, 1500);
              }}
            >
              Start keyword analysis
            </Button>
          </div>
        )}
        <Accordion type="multiple" className="w-full">
          {concepts.map(concept => (
            <AccordionItem key={concept.id} value={concept.id}>
              <AccordionTrigger className="text-left">
                <div className="flex items-center gap-3 w-full justify-between">
                  {editingConceptId === concept.id ? (
                    <div className="flex items-center gap-2 w-full">
                      <Input value={editingConceptName} onChange={e => setEditingConceptName(e.target.value)} className="max-w-sm" />
                      <Button size="sm" onClick={saveConcept}>Save</Button>
                      <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{concept.name}</span>
                      {concept.description ? <Badge variant="outline">{concept.description}</Badge> : null}
                    </div>
                  )}
                  {editingConceptId !== concept.id && (
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => startEditConcept(concept)}>Edit</Button>
                      <AlertDialog open={conceptToDelete?.id === concept.id} onOpenChange={(open) => setConceptToDelete(open ? concept : null)}>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive">Delete</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete theme and related data?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will delete the theme, its keywords and relations, and clear page assignments. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeleteConceptDeep(concept)} disabled={busy}>
                              Yes, delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  <div className="text-sm text-muted-foreground">Keywords grouped under this concept with primary and supporting pages.</div>
                  <div className="space-y-3">
                    {(keywordsByConcept[concept.id] || []).map(k => {
                      const rels = relationsByKeyword[k.id] || [];
                      const primary = rels.find(r => r.role === 'primary');
                      const supporting = rels.filter(r => r.role === 'supporting');
                      return (
                        <div key={k.id} className="border rounded-md p-3">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{k.keyword}</div>
                          </div>
                          <div className="mt-2 text-sm">
                            <div className="mb-1 font-medium">Primary page</div>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">
                                {primary && pageById[primary.sitemap_id] ? (pageById[primary.sitemap_id].title || pageById[primary.sitemap_id].url || primary.sitemap_id) : '—'}
                              </span>
                              {/* Quick actions: set another page as primary */}
                              <div className="ml-auto flex gap-2">
                                {pages.slice(0, 5).map(p => (
                                  <Button key={p.id} size="sm" variant="outline" onClick={() => setPrimaryPage(k.id, p.id)}>
                                    Set {p.title || 'page'}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 text-sm">
                            <div className="mb-1 font-medium">Supporting pages</div>
                            <div className="flex flex-wrap gap-2">
                              {supporting.map(r => (
                                <Badge key={r.id} variant="secondary" className="flex items-center gap-2">
                                  <span>{pageById[r.sitemap_id]?.title || pageById[r.sitemap_id]?.url || r.sitemap_id}</span>
                                  <Button size="sm" variant="ghost" onClick={() => removeSupporting(r.id)}>Remove</Button>
                                </Badge>
                              ))}
                              {supporting.length === 0 && <span className="text-muted-foreground">—</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {(keywordsByConcept[concept.id] || []).length === 0 && (
                      <div className="text-sm text-muted-foreground">No keywords yet for this concept.</div>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
          {/* When there are no concepts we show the message and CTA above – avoid repeating it here. */}
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default ConceptsOverview;


